import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp as MarkupIcon,
  TrendingDown as DistIcon,
  TrendingFlat as AccumIcon,
  Compress as AbsorbIcon,
} from '@mui/icons-material';
import { getPhaseConfig } from './PhaseChip';

const PHASE_ORDER = ['accumulation', 'markup', 'distribution', 'absorption'];

const PHASE_ICONS = {
  accumulation: AccumIcon,
  markup: MarkupIcon,
  distribution: DistIcon,
  absorption: AbsorbIcon,
};

export default function PhaseSummaryCards({ summary, activePhase, onPhaseClick }) {
  const { t } = useTranslation();
  if (!summary || !summary.counts) return null;

  const total = summary.total || 0;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
      {PHASE_ORDER.map((phase) => {
        const data = summary.counts[phase] || { count: 0, avg_confidence: 0 };
        const config = getPhaseConfig(phase);
        const Icon = PHASE_ICONS[phase];
        const pct = total > 0 ? (data.count / total * 100) : 0;
        const isActive = activePhase === phase;
        const confPct = (data.avg_confidence * 100).toFixed(0);

        return (
          <Tooltip
            key={phase}
            title={t(`phase.phaseDesc.${phase}`)}
            arrow
            placement="top"
            enterDelay={400}
          >
            <Box
              onClick={() => onPhaseClick?.(phase)}
              sx={{
                cursor: onPhaseClick ? 'pointer' : 'default',
                backgroundColor: isActive ? `${config.color}08` : '#fff',
                borderRadius: '12px',
                border: isActive ? `1px solid ${config.borderColor}` : '1px solid #e2e8f0',
                boxShadow: isActive ? '0 4px 14px rgba(15, 23, 42, 0.06)' : '0 1px 3px rgba(15, 23, 42, 0.04)',
                px: 1.6,
                py: 1.35,
                transition: 'all 0.2s ease',
                '&:hover': onPhaseClick ? {
                  borderColor: isActive ? config.borderColor : '#cbd5e1',
                  boxShadow: '0 6px 16px rgba(15, 23, 42, 0.07)',
                } : {},
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <Box sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isActive ? `${config.color}12` : '#f8fafc',
                    border: `1px solid ${isActive ? `${config.color}20` : '#e2e8f0'}`,
                    flexShrink: 0,
                  }}>
                    <Icon sx={{ color: config.color, fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                      fontSize: '0.9rem',
                      fontWeight: 800,
                      color: isActive ? '#0f172a' : '#334155',
                      lineHeight: 1.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {t(`phase.phases.${phase}`)}
                    </Typography>
                    <Typography sx={{
                      mt: 0.25,
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#94a3b8',
                    }}>
                      {pct.toFixed(0)}% {t('phase.ofTotal', 'tổng')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
                  <Typography sx={{
                    fontSize: '1.95rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    color: isActive ? config.color : '#0f172a',
                    fontFamily: 'monospace',
                  }}>
                    {data.count}
                  </Typography>
                  <Typography sx={{
                    mt: 0.3,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: Number(confPct) >= 30 ? config.color : '#94a3b8',
                    fontFamily: 'monospace',
                  }}>
                    {confPct}%
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 1.15 }}>
                <Box sx={{
                  height: 4,
                  borderRadius: 999,
                  backgroundColor: '#f1f5f9',
                  overflow: 'hidden',
                }}>
                  <Box sx={{
                    width: `${pct}%`,
                    height: '100%',
                    borderRadius: 999,
                    backgroundColor: config.color,
                    transition: 'width 0.6s ease',
                  }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700 }}>
                    {t('phase.detail.confidence', 'Confidence')}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: '#64748b',
                  }}>
                    {data.count} / {total}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
